import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Percent } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Checkbox } from "../ui/checkbox";
import { apiClient, getApiErrorMessage } from "../../lib/apiClient";
import { fetchTaxConfig, setTaxConfig } from "../../lib/tax";

export function TaxSettingsCard() {
  const [state, setState] = useState({ loading: true, enabled: true, rate: "5" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiClient
      .get("/outlet/tax")
      .then(({ data }) => setState({ loading: false, enabled: !!data.gst_enabled, rate: String(data.gst_rate) }))
      .catch(() => setState({ loading: false, enabled: true, rate: "5" }));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await apiClient.put("/outlet/tax", {
        gst_enabled: state.enabled,
        gst_rate: state.rate === "" ? 0 : Number(state.rate),
      });
      setTaxConfig(data);
      await fetchTaxConfig(true);
      toast.success("Tax settings saved");
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Could not save tax settings."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="settings-tax-card">
      <CardContent className="p-5">
        <div className="mb-3 flex items-center gap-2">
          <Percent size={16} strokeWidth={1.5} className="text-primary" />
          <h3 className="font-heading text-sm font-semibold text-text-main">Manage Taxes (GST)</h3>
        </div>
        <p className="mb-4 text-sm text-text-muted">
          Alberta GST is added on top of every payment amount and shown as "GST" across the app and on checkout.
        </p>

        <label className="mb-4 flex cursor-pointer items-center gap-2 text-sm text-text-main">
          <Checkbox
            checked={state.enabled}
            onCheckedChange={(v) => setState((s) => ({ ...s, enabled: Boolean(v) }))}
            data-testid="settings-tax-enabled-checkbox"
          />
          Charge GST on payments
        </label>

        <div className="flex items-end gap-3">
          <div className="w-28">
            <label className="mb-1 block text-xs text-text-muted">GST rate (%)</label>
            <div className="relative">
              <Input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={state.rate}
                disabled={!state.enabled}
                onChange={(e) => setState((s) => ({ ...s, rate: e.target.value }))}
                data-testid="settings-tax-rate-input"
              />
            </div>
          </div>
          <Button size="sm" onClick={handleSave} disabled={saving || state.loading} data-testid="settings-tax-save-button">
            {saving ? "Saving..." : "Save"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
